// Client side of the Polar checkout. Polar's /v1/checkouts/client/* endpoints are public and keyed by the
// checkout session's client secret; creating the session needs the Polar access token, so it happens on our server.

const POLAR_API_URL = 'https://api.polar.sh';
const CREATE_SESSION_URL = 'https://server.fitbuddy.co.il/api/payments/checkout-session';

export const POLAR_BUYER_TERMS_URL = 'https://polar.sh/legal/checkout-buyer-terms';

export class PolarError extends Error {
    constructor(message, status, body) {
        super(message);
        this.status = status;
        this.code = body?.error;  // e.g. 'AlreadyActiveSubscriptionError', 'TrialAlreadyRedeemed'
        this.detail = body?.detail;
    }
}

async function request(url, options) {
    const response = await fetch(url, {
        ...options,
        headers: { 'Content-Type': 'application/json', ...options?.headers },
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) {
        throw new PolarError(`${options?.method || 'GET'} ${url} failed with ${response.status}`, response.status, body);
    }
    return body;
}

export async function createCheckoutSession(email) {
    // Local testing before the server endpoint exists: paste a client secret (polar_c_...) into .env.development.local.
    const devClientSecret = process.env.REACT_APP_POLAR_DEV_CLIENT_SECRET;
    if (process.env.NODE_ENV === 'development' && devClientSecret) {
        return devClientSecret;
    }

    const { client_secret } = await request(CREATE_SESSION_URL, {
        method: 'POST',
        mode: 'cors',
        body: JSON.stringify({ email }),
    });
    return client_secret;
}

export function getCheckout(clientSecret) {
    return request(`${POLAR_API_URL}/v1/checkouts/client/${clientSecret}`);
}

export function updateCheckout(clientSecret, body) {
    return request(`${POLAR_API_URL}/v1/checkouts/client/${clientSecret}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
    });
}

export function confirmCheckout(clientSecret, body) {
    return request(`${POLAR_API_URL}/v1/checkouts/client/${clientSecret}/confirm`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// After confirm, Polar finishes the order asynchronously; wait until the session reports the outcome.
export async function waitForCheckoutResult(clientSecret, timeoutMs = 60000) {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        const checkout = await getCheckout(clientSecret);
        if (checkout.status !== 'confirmed') {
            return checkout.status;
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    return 'confirmed';
}
